import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth, useTheme } from '../context/AppContext'
import { useIsMobile } from '../hooks/useIsMobile'
import toast from 'react-hot-toast'
import { Eye, EyeOff, Sun, Moon, ArrowRight } from 'lucide-react'
import logo from '../assets/logo.png'
import api from '../services/api'
import { getSafeNextPath } from '../utils/safeRedirect'

export default function LoginPage() {
  const { login, loading } = useAuth()
  const { toggle, isDark } = useTheme()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const [form, setForm]   = useState({ username: '', password: '' })
  const [show, setShow]   = useState(false)
  const [error, setError] = useState('')
  const [focused, setFocused] = useState('')

  // En iOS, Safari hace auto-zoom al enfocar inputs con fuente < 16px.
  // En móvil forzamos 16px para evitarlo.
  const inputFontSize = isMobile ? 16 : 14

  const handleSubmit = async e => {
    e.preventDefault()
    setError('')

    // ── Validación de campos vacíos ──────────────────────────
    if (!form.username.trim()) { setError('Username is required'); return }
    if (!form.password.trim()) { setError('Password is required'); return }
    // ─────────────────────────────────────────────────────────


    try {


      const loggedUser = await login(form.username, form.password)
      toast.success('Bienvenido')
      // Redirect según rol — los partners van directo a su panel,
      // el resto entra al dashboard normal. El HomeRedirect del router
      // también cubre este caso, pero acá evitamos un flash de la
      // página de admin antes de que el redirect se aplique.
      // No hay campo `role` en la respuesta real del backend (UserOut):
      // derivamos el destino igual que AppContext (is_superuser/is_staff → admin,
      // si no y tiene company_id → partner, si no → home).
      const isAdmin = loggedUser?.is_superuser || loggedUser?.is_staff
      const isPartner = !isAdmin && !!loggedUser?.company_id

      // Django's OAuth flow (e.g. the MCP connector's Dynamic Client
      // Registration) redirects unauthenticated users here as
      // /login?next=/o/authorize/..., expecting us to bounce them back
      // once logged in. getSafeNextPath rejects anything that isn't a
      // same-origin relative path — see that file for why a naive
      // startsWith('/') check isn't enough.
      const nextTarget = getSafeNextPath(new URLSearchParams(window.location.search).get('next'))

      const target =
        loggedUser?.must_change_password ? '/force-password-change' :
        nextTarget ? nextTarget :
        isPartner ? '/partner-dashboard' :
        isAdmin   ? '/admin-leads' :
        '/'
      window.location.href = target

    } catch (err) {
      const detail = err.response?.data?.detail
      let errorMsg = 'Invalid credentials'
      
      if (typeof detail === 'string') {
        errorMsg = detail
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(d => d.msg).join(', ')
      } else if (typeof detail === 'object' && detail !== null) {
        errorMsg = JSON.stringify(detail)
      }
      
      setError(errorMsg)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg-base)',
      position: 'relative',
      overflow: 'hidden',
    }}>

      {/* ── Fondo geométrico ────────────────────────────────────── */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        {/* Línea diagonal superior */}
        <div style={{
          position: 'absolute', top: 0, right: 0,
          width: '55%', height: '100%',
          background: isDark
            ? 'linear-gradient(135deg, transparent 60%, rgba(255,255,255,0.015) 100%)'
            : 'linear-gradient(135deg, transparent 60%, rgba(0,0,0,0.018) 100%)',
        }} />
        {/* Grid de puntos */}
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: isDark ? 0.06 : 0.04 }}>
          <defs>
            <pattern id="dots" x="0" y="0" width="28" height="28" patternUnits="userSpaceOnUse">
              <circle cx="1.5" cy="1.5" r="1.5" fill="currentColor" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dots)" color="var(--text-primary)" />
        </svg>
        {/* Orbe superior derecha */}
        <div style={{
          position: 'absolute', top: '-15%', right: '-8%',
          width: 520, height: 520, borderRadius: '50%',
          background: isDark
            ? 'radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 65%)'
            : 'radial-gradient(circle, rgba(0,0,0,0.03) 0%, transparent 65%)',
        }} />
        {/* Orbe inferior izquierda */}
        <div style={{
          position: 'absolute', bottom: '-12%', left: '-6%',
          width: 400, height: 400, borderRadius: '50%',
          background: isDark
            ? 'radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 65%)'
            : 'radial-gradient(circle, rgba(0,0,0,0.025) 0%, transparent 65%)',
        }} />
      </div>

      {/* ── Toggle theme ────────────────────────────────────────── */}
      <button
        onClick={toggle}
        style={{
          position: 'absolute', top: 24, right: 24,
          width: 38, height: 38, borderRadius: '50%',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', color: 'var(--text-secondary)',
          transition: 'all var(--transition)',
          boxShadow: 'var(--shadow-sm)',
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-strong)'}
        onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
      >
        {isDark ? <Sun size={15} /> : <Moon size={15} />}
      </button>

      {/* ── Contenedor principal ─────────────────────────────────── */}
      <div style={{
        width: 'min(400px, 92vw)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        animation: 'loginEnter 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
      }}>

        {/* Logo — fuera del card, flotando arriba */}
        <div style={{
          marginBottom: 28,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0,
        }}>
          <img
            src={logo}
            alt="Logo"
            style={{
              height: 52,
              width: 'auto',
              objectFit: 'contain',
              filter: isDark ? 'brightness(0) invert(1)' : 'none',
              transition: 'filter 0.3s ease',
            }}
          />
        </div>

        {/* ── Card ─────────────────────────────────────────────── */}
        <div style={{
          width: '100%',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 20,
          padding: isMobile ? '28px 22px 24px' : '36px 36px 32px',
          boxShadow: isDark
            ? '0 0 0 1px rgba(255,255,255,0.04), 0 24px 64px rgba(0,0,0,0.4)'
            : '0 0 0 1px rgba(0,0,0,0.04), 0 24px 64px rgba(0,0,0,0.08)',
        }}>

          <form onSubmit={handleSubmit} noValidate>

            {/* Campo usuario */}
            <div style={{ marginBottom: 14 }}>
              <div style={{
                position: 'relative',
                borderRadius: 12,
                border: `1px solid ${focused === 'user' ? 'var(--border-strong)' : 'var(--border)'}`,
                background: 'var(--bg-raised)',
                transition: 'border-color var(--transition), box-shadow var(--transition)',
                boxShadow: focused === 'user'
                  ? isDark ? '0 0 0 3px rgba(255,255,255,0.06)' : '0 0 0 3px rgba(0,0,0,0.05)'
                  : 'none',
              }}>
                <label style={{
                  position: 'absolute', top: 9, left: 14,
                  fontSize: 10, fontWeight: 600, letterSpacing: '0.06em',
                  textTransform: 'uppercase', color: 'var(--text-muted)',
                  fontFamily: 'var(--font-display)',
                  pointerEvents: 'none',
                }}>
                  Email
                </label>
                <input
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  placeholder="email@example.com"
                  value={form.username}

                  onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                  onFocus={() => setFocused('user')}
                  onBlur={() => {
                    setFocused('')
                    if (!form.username.trim()) setError('Email is required')
                    else setError('')
                  }}

                  style={{
                    width: '100%', background: 'none', border: 'none', outline: 'none',
                    padding: '28px 14px 10px',
                    fontSize: inputFontSize, color: 'var(--text-primary)',
                    fontFamily: 'var(--font-body)',
                  }}
                />
              </div>
            </div>

            {/* Campo contraseña */}
            <div style={{ marginBottom: 20 }}>
              <div style={{
                position: 'relative',
                borderRadius: 12,
                border: `1px solid ${focused === 'pass' ? 'var(--border-strong)' : 'var(--border)'}`,
                background: 'var(--bg-raised)',
                transition: 'border-color var(--transition), box-shadow var(--transition)',
                boxShadow: focused === 'pass'
                  ? isDark ? '0 0 0 3px rgba(255,255,255,0.06)' : '0 0 0 3px rgba(0,0,0,0.05)'
                  : 'none',
              }}>
                <label style={{
                  position: 'absolute', top: 9, left: 14,
                  fontSize: 10, fontWeight: 600, letterSpacing: '0.06em',
                  textTransform: 'uppercase', color: 'var(--text-muted)',
                  fontFamily: 'var(--font-display)',
                  pointerEvents: 'none',
                }}>
                  Password
                </label>
                <input
                  type={show ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  onFocus={() => setFocused('pass')}
                  onBlur={() => {
                    setFocused('')
                    if (!form.password.trim()) setError('Password is required')
                    else setError('')
                  }}
                  style={{
                    width: '100%', background: 'none', border: 'none', outline: 'none',
                    padding: '28px 44px 10px 14px',
                    fontSize: inputFontSize, color: 'var(--text-primary)',
                    fontFamily: 'var(--font-body)',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShow(s => !s)}
                  style={{
                    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--text-muted)', padding: 4, display: 'flex',
                  }}
                >
                  {show ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'var(--red-bg)', color: 'var(--red)',
                fontSize: 12, padding: '10px 14px',
                borderRadius: 10, marginBottom: 16,
                border: '1px solid rgba(185,28,28,0.15)',
              }}>
                <span style={{ fontSize: 15 }}>⚠</span>
                {error}
              </div>
            )}

            {/* Botón submit */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '13px 20px',
                background: 'var(--accent)',
                color: 'var(--accent-fg)',
                border: 'none',
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                transition: 'opacity var(--transition), transform var(--transition)',
                opacity: loading ? 0.7 : 1,
                letterSpacing: '0.01em',
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.opacity = '0.88' }}
              onMouseLeave={e => { if (!loading) e.currentTarget.style.opacity = '1' }}
            >
              {loading
                ? <div className="spinner" style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} />
                : <>
                    Sign in
                    <ArrowRight size={15} />
                  </>
              }
            </button>
          </form>
        </div>

        {/* Versión debajo del card */}
        <p style={{
          marginTop: 20,
          fontSize: 11,
          color: 'var(--text-muted)',
          letterSpacing: '0.04em',
          fontFamily: 'var(--font-display)',
        }}>
          v2.0 · bestautotitleloans.com
        </p>
      </div>

      <style>{`
        @keyframes loginEnter {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}
